// Suites page — tasks and the suites that group them, on one surface
// (2026-07 nav review merged the old /tasks page in; /tasks redirects
// here). The membership relation is shown from BOTH directions: each
// suite row lists its member tasks, each task row lists the suites it
// belongs to (a task can belong to one or many suites — the SuiteTask
// join). A setup journey banner guides first-time users (tasks first,
// then suites) but the page is a normal management surface for
// returning users, not a wizard.
import { useClient } from '@cortex/react-query';
import type { Suite, Task } from '@cortex/types';
import { useQuery } from '@tanstack/react-query';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMemo, useState, type CSSProperties, type ReactNode } from 'react';

import { GraderTypeBadges } from '../../components/common/GraderTypeBadges';
import { PageHeader } from '../../components/common/PageHeader';
import { Spinner } from '../../components/common/Spinner';
import { Table, type IColumn } from '../../components/common/Table';
import { routes } from '../../lib/constants';
import { formatDateTime } from '../../lib/format';

export const Route = createFileRoute('/suites/')({ component: SuitesPage });

interface IListEnvelope<T> {
  items: T[];
  total: number;
}

type KindFilter = 'all' | 'REGRESSION' | 'CAPABILITY';

const sectionHeaderStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  gap: 'var(--sp-2)',
  margin: 'var(--sp-5) 0 var(--sp-3)',
};

const sectionTitleStyle: CSSProperties = {
  fontSize: 'var(--fs-md)',
  fontWeight: 600,
  margin: 0,
};

const chipStyle: CSSProperties = {
  fontSize: 11,
  maxWidth: 160,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
};

function SuitesPage() {
  const client = useClient();
  const nav = useNavigate();
  const [kindFilter, setKindFilter] = useState<KindFilter>('all');

  const suitesQuery = useQuery({
    queryKey: ['suites'],
    queryFn: () => client.http.get('v1/suites?limit=200').json<IListEnvelope<Suite>>(),
  });
  const tasksQuery = useQuery({
    queryKey: ['tasks'],
    queryFn: () => client.http.get('v1/tasks').json<IListEnvelope<Task>>(),
  });

  const suites = useMemo(() => suitesQuery.data?.items ?? [], [suitesQuery.data]);
  const tasks = useMemo(() => tasksQuery.data?.items ?? [], [tasksQuery.data]);

  // Membership, both directions, from the SuiteTask joins hydrated on
  // each suite (`suite.tasks` is the join rows — the source of truth the
  // eval runner reads; Task.suiteIds is a legacy mirror).
  const taskNamesById = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of tasks) m.set(t.id, t.name);
    return m;
  }, [tasks]);
  const suitesByTaskId = useMemo(() => {
    const m = new Map<string, Suite[]>();
    for (const s of suites) {
      for (const join of s.tasks) {
        const arr = m.get(join.taskId) ?? [];
        arr.push(s);
        m.set(join.taskId, arr);
      }
    }
    return m;
  }, [suites]);

  const filteredSuites = useMemo(
    () => (kindFilter === 'all' ? suites : suites.filter((s) => s.kind === kindFilter)),
    [suites, kindFilter],
  );

  const counts = useMemo(() => {
    const c = { all: suites.length, REGRESSION: 0, CAPABILITY: 0 };
    for (const s of suites) c[s.kind]++;
    return c;
  }, [suites]);

  const suiteCols: IColumn<Suite>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (s) => (
        <div>
          <Link to={routes.suiteDetail(s.id)} style={{ fontWeight: 500 }}>
            {s.name}
          </Link>
          {s.description ? (
            <div className="t-xs t-muted" style={{ marginTop: 2 }}>
              {s.description.slice(0, 80)}
              {s.description.length > 80 ? '...' : ''}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'kind',
      header: 'Kind',
      cell: (s) => (
        <span
          className="badge"
          style={{
            background:
              s.kind === 'REGRESSION' ? 'var(--status-danger-bg)' : 'var(--status-info-bg)',
            color: s.kind === 'REGRESSION' ? 'var(--status-danger)' : 'var(--status-info)',
            borderColor:
              s.kind === 'REGRESSION' ? 'var(--status-danger-bd)' : 'var(--status-info-bd)',
          }}
        >
          {s.kind}
        </span>
      ),
    },
    {
      key: 'tasks',
      header: 'Tasks',
      cell: (s) => <TaskChips suite={s} taskNamesById={taskNamesById} />,
    },
    {
      key: 'gatesRun',
      header: 'Gates',
      cell: (s) => (
        <span
          style={{
            color: s.gatesRun ? 'var(--status-success)' : 'var(--text-muted)',
            fontSize: 12,
          }}
        >
          {s.gatesRun ? '✓ Yes' : '✗ No'}
        </span>
      ),
    },
    {
      key: 'expectedPassRate',
      header: 'Expected',
      cell: (s) => (
        <span className="t-xs" style={{ fontFamily: 'var(--font-mono)' }}>
          {s.expectedPassRate != null ? `${(s.expectedPassRate * 100).toFixed(0)}%` : '—'}
        </span>
      ),
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      cell: (s) => <span className="t-xs t-muted">{formatDateTime(s.updatedAt)}</span>,
    },
  ];

  const taskCols: IColumn<Task>[] = [
    {
      key: 'name',
      header: 'Name',
      cell: (t) => (
        <div>
          <Link to={routes.taskDetail(t.id)} style={{ fontWeight: 500 }}>
            {t.name}
          </Link>
          {t.description ? (
            <div className="t-xs t-muted" style={{ marginTop: 2 }}>
              {t.description}
            </div>
          ) : null}
        </div>
      ),
    },
    {
      key: 'suites',
      header: 'In suites',
      cell: (t) => <SuiteChips suites={suitesByTaskId.get(t.id) ?? []} />,
    },
    {
      key: 'graders',
      header: 'Graders',
      cell: (t) => <GraderTypeBadges graders={t.graders} />,
    },
    {
      key: 'scoring',
      header: 'Scoring',
      cell: (t) => {
        if (!t.scoring) return <span className="t-muted">—</span>;
        return (
          <span className="t-xs" style={{ fontFamily: 'var(--font-mono)' }}>
            {t.scoring.mode}
            {t.scoring.threshold !== undefined ? ` · ${t.scoring.threshold}` : ''}
          </span>
        );
      },
    },
    {
      key: 'updatedAt',
      header: 'Updated',
      cell: (t) => <span className="t-xs t-muted">{formatDateTime(t.updatedAt)}</span>,
    },
  ];

  if (suitesQuery.isLoading || tasksQuery.isLoading) return <Spinner />;
  if (suitesQuery.error)
    return <div className="alert alert--danger">{String(suitesQuery.error)}</div>;
  if (tasksQuery.error)
    return <div className="alert alert--danger">{String(tasksQuery.error)}</div>;

  return (
    <>
      <PageHeader
        title="Suites"
        actions={
          <div style={{ display: 'flex', gap: 8 }}>
            <Link to={routes.TASKS_NEW} className="btn btn--ghost btn--sm">
              + New Task
            </Link>
            <Link to={routes.SUITES_NEW} className="btn btn--primary btn--sm">
              + New Suite
            </Link>
          </div>
        }
      />

      {tasks.length === 0 || suites.length === 0 ? (
        <SetupJourney taskCount={tasks.length} suiteCount={suites.length} />
      ) : null}

      {/* ── Suites ───────────────────────────────────────────────── */}
      <div style={sectionHeaderStyle}>
        <h2 style={sectionTitleStyle}>Suites</h2>
        <span className="t-xs t-muted">{suites.length}</span>
      </div>
      {suites.length === 0 ? (
        <div className="card" style={{ padding: '32px 24px', textAlign: 'center' }}>
          <div className="t-secondary">
            No suites yet — group your tasks into a suite to run them together.
          </div>
        </div>
      ) : (
        <>
          <div style={{ marginBottom: 16, display: 'flex', gap: 0 }}>
            {(
              [
                ['all', `All · ${counts.all}`],
                ['REGRESSION', `Regression · ${counts.REGRESSION}`],
                ['CAPABILITY', `Capability · ${counts.CAPABILITY}`],
              ] as [KindFilter, string][]
            ).map(([k, label]) => (
              <button
                key={k}
                className="btn btn--sm"
                style={{
                  borderRadius: 0,
                  borderRight: k !== 'CAPABILITY' ? 'none' : undefined,
                  background: kindFilter === k ? 'var(--surface-2)' : 'transparent',
                  fontWeight: kindFilter === k ? 600 : 400,
                  ...(k === 'all'
                    ? {
                        borderTopLeftRadius: 'var(--radius-sm)',
                        borderBottomLeftRadius: 'var(--radius-sm)',
                      }
                    : {}),
                  ...(k === 'CAPABILITY'
                    ? {
                        borderTopRightRadius: 'var(--radius-sm)',
                        borderBottomRightRadius: 'var(--radius-sm)',
                      }
                    : {}),
                }}
                onClick={() => setKindFilter(k)}
              >
                {label}
              </button>
            ))}
          </div>

          <Table
            rows={filteredSuites}
            columns={suiteCols}
            rowKey={(s) => s.id}
            onRowClick={(s) => nav({ to: routes.suiteDetail(s.id) })}
          />
        </>
      )}

      {/* ── Tasks ────────────────────────────────────────────────── */}
      <div style={sectionHeaderStyle}>
        <h2 style={sectionTitleStyle}>Tasks</h2>
        <span className="t-xs t-muted">{tasks.length}</span>
        <span className="t-xs t-muted" style={{ marginLeft: 'auto' }}>
          Reusable test cases — add one to a suite from the suite&apos;s detail page or the task
          editor.
        </span>
      </div>
      {tasks.length === 0 ? (
        <div className="card" style={{ padding: '32px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 15, fontWeight: 500, marginBottom: 8 }}>No tasks yet</div>
          <div className="t-secondary" style={{ marginBottom: 16 }}>
            Create your first task to start measuring agent quality.
          </div>
          <Link to={routes.TASKS_NEW} className="btn btn--primary btn--sm">
            + New Task
          </Link>
        </div>
      ) : (
        <Table
          rows={tasks}
          columns={taskCols}
          rowKey={(t) => t.id}
          onRowClick={(t) => nav({ to: routes.taskDetail(t.id) })}
        />
      )}
    </>
  );
}

function TaskChips({ suite, taskNamesById }: { suite: Suite; taskNamesById: Map<string, string> }) {
  if (suite.tasks.length === 0) {
    return <span className="t-xs t-muted">none</span>;
  }
  const visible = suite.tasks.slice(0, 3);
  const overflow = suite.tasks.length - visible.length;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
      {visible.map((join) => {
        const name = taskNamesById.get(join.taskId) ?? join.taskId.slice(-6);
        return (
          <Link
            key={join.taskId}
            to={routes.taskDetail(join.taskId)}
            className="badge badge--neutral"
            style={chipStyle}
            title={name}
            onClick={(e) => e.stopPropagation()}
          >
            {name}
          </Link>
        );
      })}
      {overflow > 0 ? (
        <span className="badge badge--neutral" style={chipStyle} title={`${overflow} more tasks`}>
          +{overflow}
        </span>
      ) : null}
    </div>
  );
}

function SuiteChips({ suites }: { suites: Suite[] }) {
  if (suites.length === 0) {
    return (
      <span className="t-xs t-muted" title="Add it from a suite's detail page or the task editor">
        not in a suite
      </span>
    );
  }
  const visible = suites.slice(0, 3);
  const overflow = suites.length - visible.length;
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, alignItems: 'center' }}>
      {visible.map((s) => (
        <Link
          key={s.id}
          to={routes.suiteDetail(s.id)}
          className="badge badge--neutral"
          style={chipStyle}
          title={s.name}
          onClick={(e) => e.stopPropagation()}
        >
          {s.name}
        </Link>
      ))}
      {overflow > 0 ? (
        <span className="badge badge--neutral" style={chipStyle} title={`${overflow} more suites`}>
          +{overflow}
        </span>
      ) : null}
    </div>
  );
}

// First-run guidance: tasks first, then suites, then run an eval. Shown
// only while either list is empty — returning users manage everything
// from the two sections below without wizard chrome.
function SetupJourney({ taskCount, suiteCount }: { taskCount: number; suiteCount: number }) {
  const steps: { title: string; body: string; done: boolean; cta: ReactNode }[] = [
    {
      title: '1 · Create tasks',
      body: 'A task is one reusable test case: input, graders, scoring policy.',
      done: taskCount > 0,
      cta: (
        <Link to={routes.TASKS_NEW} className="btn btn--primary btn--sm">
          + New Task
        </Link>
      ),
    },
    {
      title: '2 · Group them into suites',
      body: 'A suite bundles tasks for capability testing or regression gating. Tasks can join many suites.',
      done: suiteCount > 0,
      cta: (
        <Link to={routes.SUITES_NEW} className="btn btn--primary btn--sm">
          + New Suite
        </Link>
      ),
    },
    {
      title: '3 · Run an eval',
      body: 'Point an evaluation at a suite to score a workflow version against every member task.',
      done: false,
      cta: (
        <Link to={routes.EVALS_NEW} className="btn btn--ghost btn--sm">
          New eval →
        </Link>
      ),
    },
  ];
  // The current step is the first not-done one; later steps stay muted.
  const currentIdx = steps.findIndex((s) => !s.done);
  return (
    <div
      className="card"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))',
        gap: 'var(--sp-4)',
        padding: 'var(--sp-4)',
        marginBottom: 'var(--sp-4)',
      }}
    >
      {steps.map((s, i) => (
        <div
          key={s.title}
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--sp-2)',
            opacity: s.done || i === currentIdx ? 1 : 0.55,
          }}
        >
          <span style={{ fontWeight: 600, fontSize: 13 }}>
            {s.title}
            {s.done ? (
              <span style={{ color: 'var(--status-success)', marginLeft: 6 }}>✓</span>
            ) : null}
          </span>
          <span className="t-xs t-secondary">{s.body}</span>
          {!s.done && i === currentIdx ? <div>{s.cta}</div> : null}
        </div>
      ))}
    </div>
  );
}
